import { OptionInfoDto } from "../../../screens/options-table/types/OptionInfoDto";
import { OrderModel } from "../../../screens/portfolio-info/types/OrderModel";
import { useSelectedPortfolioService } from "./useSelectedPortfolioService";
import { portfolioMappers } from "../../mappers/PortfolioMappers";
import { AutoOrderConfigurationDto } from "../../../screens/portfolio-info/types/AutoOrderConfigurationDto";
import { AutoOrderConfigurationModel } from "../../../screens/portfolio-info/types/AutoOrderConfigurationModel";
import { useAuthContext } from "../../../screens/authentication/hooks/use-auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { createAuthenticatedApiClient } from "../../services/api/AuthenticatedApiClient";
import { createOrderApi } from "../../services/api/OrderApi";
import {
    ordersService,
    PositionCustomImpliedVolatilityUpdate,
} from "../../services/orders-service/OrdersService";
import { portfolioModelService } from "../../services/portfolio-model-service/PortfolioModelService";
import { TargetPlatform } from "../../types/TargetPlatform";

export function useOrdersService() {
    const {
        marketDataDto,
        portfolioGroups,
        selectedPortfolioGroup,
        portfolios,
        underlyingAssetPortfolios,
        selectedPortfolio,
        isAllPortfoliosSelected,
        createPortfolioGroup,
        editSelectedPortfolioGroup,
        deleteSelectedPortfolioGroup,
        createPortfolio,
        portfolioProfile,
        comparedPortfolioProfile,
        setSelectedPortfolio,
        setSelectedPortfolioGroupByName,
        setSelectedPortfolioByName,
        setAllPortfoliosByUnderlyingAsset,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        savePortfolio,
        fetchSelectedPortfolio,
        deleteSelectedPortfolio,
        refreshPortfolioProfile,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshComparedPortfolioProfile,
        setComparedPortfolioProfile,
        cloneSelectedPortfolio,
        selectedPortfolioDates,
        saveCustomImpliedVolatility,
    } = useSelectedPortfolioService();
    
    const { userAuthData, isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();
    const apiClient = useMemo(
        () => createAuthenticatedApiClient({ userAuthData, isUserAuthenticated, navigate }),
        [userAuthData, isUserAuthenticated, navigate],
    );
    const orderApi = useMemo(() => createOrderApi(apiClient), [apiClient]);

    function addOptionToPortfolio(item: OptionInfoDto, size: number): void {
        if (!selectedPortfolio || isAllPortfoliosSelected) {
            return;
        }

        const orderModel = ordersService.createOptionOrder(
            item,
            size,
            marketDataDto?.UnderlyingPrice ?? 0,
        );
        const updatedPortfolio = ordersService.addOrder(selectedPortfolio, orderModel);
        
        setSelectedPortfolio(updatedPortfolio);
        refreshPortfolioProfile(updatedPortfolio);
    }

    // todo: addOrderToPortfolio
    async function addFutureToPortfolio(item: OrderModel): Promise<void> {
        await addFuturesToPortfolio([item]);
    }

    async function addFuturesToPortfolio(items: OrderModel[]): Promise<void> {
        if (!selectedPortfolio || isAllPortfoliosSelected || items.length === 0) {
            return;
        }
        
        const portfolioWithNewItem = ordersService.addOrders(selectedPortfolio, items);
        const updatedPortfolio = marketDataDto
            ? ordersService.refreshOrdersParameters(portfolioWithNewItem, marketDataDto)
            : portfolioWithNewItem;

        setSelectedPortfolio(updatedPortfolio);
        refreshPortfolioProfile(updatedPortfolio);
        await savePortfolio(updatedPortfolio);
    }
    
    async function updateSelectedPortfolio(): Promise<void> {
        if (!selectedPortfolio) {
            return;
        }

        const updatedPortfolio = portfolioModelService.copyPortfolio(selectedPortfolio);

        setSelectedPortfolio(updatedPortfolio);
        refreshPortfolioProfile(updatedPortfolio);
        
        if (selectedComparedPortfolio !== null) {
            setComparedPortfolioProfile(comparedPortfolioProfile);
            refreshComparedPortfolioProfile(updatedPortfolio, selectedComparedPortfolio);
        }
    }
    
    async function updatePositionInSelectedPortfolio(
        optionSymbol: string,
        targetPlatform: TargetPlatform,
        customImpliedVolatility: number,
        customImpliedVolatilityStep?: number | null,
    ): Promise<void> {
        await updatePositionsInSelectedPortfolio(
            optionSymbol,
            [{
                targetPlatform,
                customImpliedVolatility,
                customImpliedVolatilityStep,
            }],
        );
    }

    async function updatePositionsInSelectedPortfolio(
        optionSymbol: string,
        updates: PositionCustomImpliedVolatilityUpdate[],
    ): Promise<void> {
        if (!selectedPortfolio || isAllPortfoliosSelected) {
            return;
        }

        const updatedPortfolio = ordersService.updateOptionCustomImpliedVolatilities(
            selectedPortfolio,
            optionSymbol,
            updates,
        );
        
        setSelectedPortfolio(updatedPortfolio);
        refreshPortfolioProfile(updatedPortfolio);
    }
    
    async function updateOrderInSelectedPortfolio(
        refreshOrderParameters: boolean = true,
        saveToStorage: boolean = true,
    ): Promise<void> {
        if (!selectedPortfolio || isAllPortfoliosSelected) {
            return;
        }
        
        const updatedPortfolio = refreshOrderParameters && marketDataDto
            ? ordersService.refreshOrdersParameters(selectedPortfolio, marketDataDto)
            : portfolioModelService.copyPortfolio(selectedPortfolio, { modifiedAt: new Date() });
        
        setSelectedPortfolio(updatedPortfolio);
        refreshPortfolioProfile(updatedPortfolio);
        
        if (saveToStorage) {
            await savePortfolio(updatedPortfolio);
        }
    }

    async function saveAutoOrderConfiguration(
        orderId: number,
        autoOrderConfiguration: AutoOrderConfigurationDto,
    ): Promise<AutoOrderConfigurationModel | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        if (orderId <= 0) {
            return null;
        }

        const configuration = await orderApi.saveAutoOrderConfiguration(
            orderId,
            autoOrderConfiguration,
        );

        return portfolioMappers.mapAutoOrderConfiguration(configuration);
    }

    async function fetchAutoOrderConfiguration(orderId: number): Promise<AutoOrderConfigurationModel | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        if (orderId <= 0) {
            return null;
        }

        const configuration = await orderApi.fetchAutoOrderConfiguration(orderId);

        return portfolioMappers.mapAutoOrderConfiguration(configuration);
    }

    async function resetAutoOrderConfigurationState(orderId: number): Promise<AutoOrderConfigurationModel | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        if (orderId <= 0) {
            return null;
        }

        const configuration = await orderApi.resetAutoOrderConfigurationState(orderId);

        return portfolioMappers.mapAutoOrderConfiguration(configuration);
    }
    
    async function deleteOrderFromSelectedPortfolio(internalId: string): Promise<void> {
        if (!selectedPortfolio || isAllPortfoliosSelected) {
            return;
        }

        const updatedPortfolio = ordersService.deleteOrder(selectedPortfolio, internalId);
        
        setSelectedPortfolio(updatedPortfolio);
        refreshPortfolioProfile(updatedPortfolio);
        
        await savePortfolio(updatedPortfolio);
    }

    return {
        marketDataDto,
        portfolioGroups,
        selectedPortfolioGroup,
        portfolios,
        underlyingAssetPortfolios,
        selectedPortfolio,
        isAllPortfoliosSelected,
        createPortfolioGroup,
        editSelectedPortfolioGroup,
        deleteSelectedPortfolioGroup,
        createPortfolio,
        portfolioProfile,
        comparedPortfolioProfile,
        setSelectedPortfolio,
        setSelectedPortfolioGroupByName,
        setSelectedPortfolioByName,
        setAllPortfoliosByUnderlyingAsset,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        fetchSelectedPortfolio,
        deleteSelectedPortfolio,
        refreshPortfolioProfile,
        addOptionToPortfolio,
        addFutureToPortfolio,
        addFuturesToPortfolio,
        updateSelectedPortfolio,
        updateOrderInSelectedPortfolio,
        deleteOrderFromSelectedPortfolio,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        cloneSelectedPortfolio,
        selectedPortfolioDates,
        updatePositionInSelectedPortfolio,
        updatePositionsInSelectedPortfolio,
        saveCustomImpliedVolatility,
        saveAutoOrderConfiguration,
        fetchAutoOrderConfiguration,
        resetAutoOrderConfigurationState,
    };
}
